@if (state) {

    <!-- ============================================================ -->
    <!-- PAGE HEADER                                                   -->
    <!-- ============================================================ -->
    <div class="mb-4">
        <h3 class="text-xl font-semibold m-0 mb-1">Zusammenfassung</h3>
        <p class="text-color-secondary text-sm m-0">Überprüfen Sie Ihre Konfiguration vor dem Erstellen.</p>
    </div>

    <!-- ============================================================ -->
    <!-- BASICS SUMMARY                                                -->
    <!-- ============================================================ -->
    <div class="border-1 border-200 border-round-lg mb-3 overflow-hidden">
        <div class="flex align-items-center justify-content-between px-3 py-2 surface-50 border-bottom-1 border-200">
            <div class="flex align-items-center gap-2">
                <i class="fa-regular fa-circle-info text-primary"></i>
                <span class="font-semibold">Grundlagen</span>
            </div>
            <button pButton
                    icon="fa-regular fa-pen-to-square"
                    class="p-button-text p-button-sm p-button-rounded"
                    (click)="editStep(1)"
                    pTooltip="Bearbeiten">
            </button>
        </div>

        <div class="p-3">
            <div class="grid row-gap-3">
                <div class="col-12 md:col-6">
                    <div class="text-color-secondary text-sm mb-1">Name</div>
                    <div class="font-medium">{{ notificationType?.name }}</div>
                </div>
                <div class="col-12 md:col-6">
                    <div class="text-color-secondary text-sm mb-1">Typ</div>
                    <div class="flex align-items-center gap-2">
                        <i [class]="typeIcon + ' text-sm text-primary'"></i>
                        <span class="font-medium">{{ typeLabel }}</span>
                    </div>
                </div>
                <div class="col-12 md:col-6">
                    <div class="text-color-secondary text-sm mb-1">Gruppe</div>
                    <div class="font-medium">{{ notificationType?.groupDtoName || '—' }}</div>
                </div>
                <div class="col-12 md:col-6">
                    <div class="text-color-secondary text-sm mb-1">Status</div>
                    <div class="flex align-items-center gap-2">
                        @if (notificationType?.active !== false) {
                            <p-tag value="Aktiv" severity="success"></p-tag>
                        } @else {
                            <p-tag value="Inaktiv" severity="danger"></p-tag>
                        }
                        @if (notificationType?.suspendedSend) {
                            <p-tag value="Freigabe erforderlich" severity="warn"></p-tag>
                        }
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- CHANNELS SUMMARY                                              -->
    <!-- ============================================================ -->
    <div class="border-1 border-200 border-round-lg mb-3 overflow-hidden">
        <div class="flex align-items-center justify-content-between px-3 py-2 surface-50 border-bottom-1 border-200">
            <div class="flex align-items-center gap-2">
                <i class="fa-regular fa-tower-broadcast text-primary"></i>
                <span class="font-semibold">Versandkanäle</span>
                <p-tag [value]="channelCount.toString()" [rounded]="true" severity="secondary"></p-tag>
            </div>
            @if (!isCopyMode) {
                <button pButton
                        icon="fa-regular fa-pen-to-square"
                        class="p-button-text p-button-sm p-button-rounded"
                        (click)="editStep(2)"
                        pTooltip="Bearbeiten">
                </button>
            }
        </div>

        <div class="p-3">
            @if (channels.length) {
                <div class="grid">
                    @for (channel of channels; track channel.id) {
                        <div class="col-12 lg:col-6">
                            <div class="flex align-items-center justify-content-between p-2 surface-100 border-round-lg h-full gap-2">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-regular fa-circle-check text-green-500"></i>
                                    <span class="font-medium">{{ channel.channelTypeDtoName }}</span>
                                </div>
                                @if (channel.templateDtoName) {
                                    <span class="text-color-secondary text-sm text-right">
                                        Vorlage: {{ channel.templateDtoName }}
                                    </span>
                                }
                            </div>
                        </div>
                    }
                </div>
            } @else {
                <div class="flex align-items-center gap-2 p-3 bg-yellow-50 border-round-lg">
                    <i class="fa-regular fa-triangle-exclamation text-yellow-600"></i>
                    <span class="text-yellow-700 text-sm">Keine Kanäle konfiguriert.</span>
                </div>
            }
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- FIELDS SUMMARY                                                -->
    <!-- ============================================================ -->
    <div class="border-1 border-200 border-round-lg mb-3 overflow-hidden">
        <div class="flex align-items-center justify-content-between px-3 py-2 surface-50 border-bottom-1 border-200">
            <div class="flex align-items-center gap-2">
                <i class="fa-regular fa-tags text-primary"></i>
                <span class="font-semibold">Felder</span>
                <p-tag [value]="fieldCount.toString()" [rounded]="true" severity="secondary"></p-tag>
            </div>
            @if (!isCopyMode) {
                <button pButton
                        icon="fa-regular fa-pen-to-square"
                        class="p-button-text p-button-sm p-button-rounded"
                        (click)="editStep(2.5)"
                        pTooltip="Bearbeiten">
                </button>
            }
        </div>

        <div class="p-3">
            @if (fields.length) {
                <div class="grid">
                    @for (field of fields; track field.id) {
                        <div class="col-12 md:col-6">
                            <div class="flex align-items-center justify-content-between p-2 surface-100 border-round-lg h-full">
                                <div class="flex align-items-center gap-2">
                                    <i class="fa-regular fa-circle-check text-green-500"></i>
                                    <span class="font-medium">{{ field.name }}</span>
                                    @if (field.mandatory) {
                                        <span class="text-red-500 font-medium">*</span>
                                    }
                                </div>
                                <span class="text-color-secondary text-sm">{{ field.fieldTypeDtoName }}</span>
                            </div>
                        </div>
                    }
                </div>
            } @else {
                <div class="flex align-items-center gap-2 p-3 bg-yellow-50 border-round-lg">
                    <i class="fa-regular fa-triangle-exclamation text-yellow-600"></i>
                    <span class="text-yellow-700 text-sm">Keine Felder definiert.</span>
                </div>
            }
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- CONFIG CHECK                                                  -->
    <!-- ============================================================ -->
    <div class="border-1 border-200 border-round-lg mb-3 overflow-hidden">
        <div class="flex align-items-center justify-content-between px-3 py-2 surface-50 border-bottom-1 border-200">
            <div class="flex align-items-center gap-2">
                <i class="fa-regular fa-clipboard-check text-primary"></i>
                <span class="font-semibold">Konfigurationsprüfung</span>
            </div>
            <button pButton
                    icon="fa-solid fa-rotate"
                    class="p-button-text p-button-sm p-button-rounded"
                    (click)="refreshCheck()"
                    [loading]="configCheckLoading"
                    pTooltip="Erneut prüfen">
            </button>
        </div>

        <div class="p-3">
            <ns-wizard-config-check
                [result]="configCheckResult"
                [loading]="configCheckLoading"
                [isCopyMode]="isCopyMode"
                (onRefresh)="refreshCheck()"
                (onNavigateToStep)="editStep($event)"
            ></ns-wizard-config-check>
        </div>
    </div>

    <!-- ============================================================ -->
    <!-- HINT                                                          -->
    <!-- ============================================================ -->
    <div class="flex align-items-start gap-3 p-3 bg-blue-50 border-round-lg border-1 border-blue-200">
        <i class="fa-regular fa-lightbulb text-blue-500 text-lg mt-1"></i>
        <div class="flex flex-column gap-1">
            <span class="font-medium text-blue-700 text-sm">Hinweis</span>
            <span class="text-sm text-blue-600">
                Erweiterte Einstellungen wie Dokumentenanhänge, Variablen-Zuordnung und Kontakttypen
                können nach dem Erstellen auf der Detailseite konfiguriert werden.
            </span>
        </div>
    </div>
}
